import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { LocationEntity } from './location.entity';

export enum SensorType {
	PROXIMITY_LEVEL = 'proximity_level',
	TEMPERATURE = 'temperature',
	HUMIDITY = 'humidity',
}

export enum SensorStatus {
	ONLINE = 'online',
	OFFLINE = 'offline',
	ERROR = 'error',
	MAINTENANCE = 'maintenance',
}

@Index('sensor_device_module_uindex', ['deviceId', 'moduleId'], {
	unique: true,
})
@Index('sensor_location_idx', ['locationId'])
@Entity('sensor')
export class SensorEntity extends BaseEntity {
	@Column('varchar', { name: 'device_id', length: 128 })
	public deviceId!: string;

	@Column('smallint', {
		name: 'module_id',
		unsigned: true,
		default: 1,
	})
	public moduleId!: number;

	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name!: string | null;

	@Column('enum', {
		name: 'type',
		enum: SensorType,
		default: SensorType.PROXIMITY_LEVEL,
	})
	public type!: SensorType;

	@Column('int', {
		name: 'fk_location__id',
		unsigned: true,
		nullable: true,
	})
	public locationId!: number | null;

	@Column('enum', {
		name: 'status',
		enum: SensorStatus,
		default: SensorStatus.OFFLINE,
	})
	public status!: SensorStatus;

	@Column('boolean', { name: 'power_on', default: false })
	public powerOn!: boolean;

	@Column('datetime', { name: 'last_seen', nullable: true })
	public lastSeen!: Date | null;

	@Column('varchar', { name: 'firmware_version', nullable: true, length: 64 })
	public firmwareVersion!: string | null;

	@Column('varchar', { name: 'hardware_model', nullable: true, length: 128 })
	public hardwareModel!: string | null;

	@Column('text', { name: 'last_error_message', nullable: true })
	public lastErrorMessage!: string | null;

	// --- Item mapping & calibration ---

	@Column('int', {
		name: 'fk_item__id',
		unsigned: true,
		nullable: true,
	})
	public itemId!: number | null;

	// Distance reading (mm) when the zone is completely empty (sensor to floor).
	// This is the maximum distance the sensor will report and represents 0% fill.
	@Column('int', { name: 'zone_depth_mm', nullable: true })
	public zoneDepthMm!: number | null;

	// Distance reading (mm) when the zone is considered full.
	// Accounts for sensor offset from the top of the zone. Default 0 means
	// product reaches the sensor when full. Set higher if there's a gap.
	@Column('int', { name: 'min_distance_mm', nullable: true, default: 0 })
	public minDistanceMm!: number | null;

	// --- Relations ---

	@ManyToOne(() => LocationEntity)
	@JoinColumn({ name: 'fk_location__id' })
	public fkLocation!: LocationEntity;

	@ManyToOne(() => ItemEntity)
	@JoinColumn({ name: 'fk_item__id' })
	public fkItem!: ItemEntity;
}
